import React from 'react';
import cloudVariants from './TransientThoughtCloudVariants';
import bubbleVariants from './TransientThoughtBubbleVariants';
import { getColor, getTextColor } from '@services/utils.service';

// Stable per-index position picks so each comment lives at a consistent slot when scrolling.
const BUBBLE_POSITIONS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];
const CLOUD_POSITIONS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

const getCloudSize = (text) => {
  const length = text?.length || 0;
  if (length < 34) return 'small';
  if (length < 100) return 'medium';
  return 'large';
};

// Mirrors the sizing tiers from TransientThoughtSpeechBubble.
const getBubbleSize = (text) => {
  const length = text?.length || 0;
  if (length < 25) return 'small';
  if (length < 60) return 'medium';
  if (length < 120) return 'large';
  if (length < 200) return 'xl';
  return 'xxl';
};

// Match the live/active cloud intrinsic sizes so the review shows comments at the
// same size they appeared on stage. Live clouds render their SVG at natural size
// (TransientThoughtCloud → cloudVariants: small 904, medium 1096, large 1096); the
// % cap keeps narrow columns from overflowing.
const CLOUD_CONTAINER_WIDTH = {
  small: 'min(904px, 92%)',
  medium: 'min(1096px, 92%)',
  large: 'min(1096px, 92%)',
};

// Match the live SpeechBubble DISPLAY_WIDTH (large container) so the review shows
// bubbles at the same size they appeared on stage. (getBubbleSize tiers map 1:1 to
// the live getTextTier: small→short, medium→medium, large→long, xl→xl, xxl→xxl.)
const BUBBLE_CONTAINER_WIDTH = {
  small: 'min(460px, 88%)',
  medium: 'min(760px, 88%)',
  large: 'min(920px, 88%)',
  xl: 'min(1080px, 88%)',
  xxl: 'min(1260px, 92%)',
};

// Font cap mirroring the live bubble's maxFontPx = displayWidth × 0.078, so the
// text renders at the same size as on stage rather than overflowing/oversizing.
const BUBBLE_MAX_FONT_PX = {
  small: 36, medium: 59, large: 72, xl: 84, xxl: 98,
};

// Line clamp per tier — same progression as the live bubble.
const BUBBLE_LINE_CLAMP = { small: 2, medium: 3, large: 4, xl: 5, xxl: 6 };

const StaticBubble = ({ positionKey, fillColor, text, textColor, fontFamily, fontSize, username, showUsername }) => {
  const isLeft = positionKey.includes('Left');
  const alignment = isLeft ? 'justify-start' : 'justify-end';

  // Left side: br variant (tail at top-left, points toward left speaker)
  // Right side: tl variant (tail droops bottom-right, points toward right speaker)
  const variantId = isLeft ? 'br' : 'tl';
  const size = getBubbleSize(text);
  const rawSvg = bubbleVariants[`${size}-${variantId}`]?.(fillColor);
  const svg = rawSvg && React.cloneElement(rawSvg, {
    width: '100%',
    height: 'auto',
    preserveAspectRatio: 'xMidYMid meet',
    style: { ...(rawSvg.props.style || {}), display: 'block', maxWidth: '100%' },
  });

  const containerWidth = BUBBLE_CONTAINER_WIDTH[size];
  const lineClamp = BUBBLE_LINE_CLAMP[size];
  const effectiveFontSize = `min(${fontSize}, ${BUBBLE_MAX_FONT_PX[size]}px)`;

  // Badge on tl protrudes below (-bottom-8 = 32px); br protrudes above (-top-10 = 40px).
  // Use overflow-x-hidden so the badge isn't clipped vertically, and add matching padding.
  const topPad = showUsername && username && variantId === 'br' ? 'pt-12' : 'pt-2';
  const bottomPad = showUsername && username && variantId === 'tl' ? 'pb-10' : 'pb-2';

  return (
    <div className={`relative w-full flex ${alignment} overflow-x-hidden ${topPad} ${bottomPad}`}>
      <div className="relative" style={{ width: containerWidth }}>
        <div style={{ filter: `drop-shadow(2px 4px 1px ${typeof fillColor === 'string' && fillColor.startsWith('linear') ? '#FABBFF' : fillColor})` }}>
          {svg}
        </div>
        <div
          lang="en"
          style={{ color: textColor, fontFamily, fontSize: effectiveFontSize, lineHeight: 1.25, wordWrap: 'break-word' }}
          className={`absolute top-[8%] left-0 right-0 flex items-center justify-center text-center px-[12%] ${['xl', 'xxl'].includes(size) ? 'bottom-[16%]' : 'bottom-[10%]'}`}
        >
          <span style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lineClamp,
            overflow: 'hidden',
          }}>
            {text}
          </span>
        </div>
        {showUsername && username && (
          <UserName name={username} fillColor={fillColor} variantId={variantId} />
        )}
      </div>
    </div>
  );
};

// Username pill — matches the position used in TransientThoughtSpeechBubble.
const UserName = ({ name, fillColor, variantId, positionClass: positionClassProp }) => {
  if (!name) return null;
  // tl bubble: tail droops toward bottom-right → badge below, right-aligned
  // br bubble: tail points toward top-left → badge above, left-aligned
  const positionMap = {
    tl: 'absolute -bottom-8 right-2',
    br: 'absolute -top-10 left-2',
  };
  const positionClass = positionClassProp ?? positionMap[variantId] ?? 'absolute -bottom-8 right-2';
  const isGradient = fillColor && typeof fillColor === 'string' && fillColor.startsWith('linear-gradient');
  const avatarStyle = isGradient ? { background: fillColor } : { backgroundColor: fillColor };
  return (
    <div
      style={{
        padding: '4px 12px 4px 4px',
        width: 'fit-content',
        borderRadius: '4px',
        border: '1px solid #333333',
        background: 'rgba(34, 34, 34, 0.8)',
        height: 'fit-content'
      }}
      className={`flex items-center ${positionClass}`}
    >
      <div style={avatarStyle} className="text-white px-2 rounded-sm mr-2 text-base">
        {name[0].toUpperCase()}
      </div>
      <p className="text-white text-base">{name}</p>
    </div>
  );
};

const CLOUD_BADGE_POSITION = {
  tl: 'absolute -top-7 left-2',
  tr: 'absolute -top-7 right-2',
  bl: 'absolute -bottom-8 left-2',
  br: 'absolute -bottom-8 right-2',
};

const StaticCloud = ({ positionKey, fillColor, text, textColor, fontFamily, fontSize, username, showUsername }) => {
  const size = getCloudSize(text);
  const idMap = { topLeft: 'tl', topRight: 'tr', bottomLeft: 'bl', bottomRight: 'br' };
  const variantId = idMap[positionKey] || 'tl';
  const key = `${size}-${variantId}`;
  const fallbackKey = `medium-${variantId}`;
  const rawSvg = (cloudVariants[key] || cloudVariants[fallbackKey])?.(fillColor);
  const svg = rawSvg && React.cloneElement(rawSvg, {
    width: '100%',
    height: 'auto',
    preserveAspectRatio: 'xMidYMid meet',
    style: { ...(rawSvg.props.style || {}), display: 'block', maxWidth: '100%' }
  });
  const isLeft = positionKey.includes('Left');
  const alignment = isLeft ? 'justify-start' : 'justify-end';

  const badgeIsAbove = showUsername && username && ['tl', 'tr'].includes(variantId);
  const badgeIsBelow = showUsername && username && ['bl', 'br'].includes(variantId);
  const topPad = badgeIsAbove ? 'pt-9' : 'pt-2';
  const bottomPad = badgeIsBelow ? 'pb-10' : 'pb-2';

  return (
    <div className={`relative w-full flex ${alignment} overflow-x-hidden ${topPad} ${bottomPad}`}>
      <div className="relative" style={{ width: CLOUD_CONTAINER_WIDTH[size] }}>
        <div style={{ filter: `drop-shadow(2px 4px 1px ${typeof fillColor === 'string' && fillColor.startsWith('linear') ? '#FABBFF' : fillColor})` }}>
          {svg}
        </div>
        <div
          lang="en"
          style={{
            color: textColor, fontFamily, fontSize, lineHeight: 1.2
          }}
          className="absolute inset-0 flex items-center justify-center text-center px-[12%]"
        >
          <span style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 5,
            overflow: 'hidden',
            wordWrap: 'break-word'
          }}>
            {text}
          </span>
        </div>
        {showUsername && (
          <UserName name={username} fillColor={fillColor} positionClass={CLOUD_BADGE_POSITION[variantId]} />
        )}
      </div>
    </div>
  );
};

// Map short bubble/cloud config ids ('tr', 'cl', etc.) to camelCase position keys used here.
const ID_TO_POSITION = {
  tl: 'topLeft', tr: 'topRight', bl: 'bottomLeft', br: 'bottomRight', cl: 'centerLeft', cr: 'centerRight'
};

// Primitive props only (words/username strings, index, flags) so React.memo's
// shallow compare keeps history items from re-rendering when the poll refetches.
const TransientThoughtFeedItem = ({
  words, username, index, isBubbleStyle, colors, fontFamily, fontSize, transientThoughtShowUsername,
  positionKey: positionKeyProp
}) => {
  const positions = CLOUD_POSITIONS;
  const resolvedPosition = positionKeyProp && (ID_TO_POSITION[positionKeyProp] || positionKeyProp);
  const positionKey = resolvedPosition || positions[index % positions.length];

  const fillColor = colors?.ttmultipleColors
    ? getColor(colors, index, colors?.ttBgColor)
    : (index % 2 === 0 ? colors?.chatBubble1 : colors?.chatBubble2) || '#FABBFF';

  const textColor = colors?.ttmultipleColors
    ? getTextColor(colors, index)
    : colors?.ttTextColor1 || '#000000';

  const text = words || '';
  const Component = isBubbleStyle ? StaticBubble : StaticCloud;

  return (
    <div className="w-full py-3">
      <Component
        positionKey={positionKey}
        fillColor={fillColor}
        text={text}
        textColor={textColor}
        fontFamily={fontFamily}
        fontSize={fontSize}
        username={username}
        showUsername={transientThoughtShowUsername}
      />
    </div>
  );
};

export default React.memo(TransientThoughtFeedItem);
